import { logAndCaptureMessage } from './errorTools'

// Two focus traps that each re-focus their own container will bounce focus between each other
// forever. In WebKit `element.focus()` dispatches focus events synchronously, so every bounce nests
// another frame instead of unwinding and the stack overflows, killing the page's JS mid-render —
// the shopper is left on a dead page and has to reload. That is sc-145026 / DFH-CORP-SITES-HRD:
// Radix's FocusScope (every Dialog/Sheet/Popover/Select in @dfh-corp-sites/ui) against the Ketch
// consent banner's own trap. Neither side can be fixed from the other, so break the cycle centrally.
//
// This sits alongside chunkReload for the same reason: the fault happens outside React's render, so
// no error boundary can see it, and a global listener installed before React mounts is the only place
// that can intervene.
const TRIPPED_MESSAGE = 'Focus thrash guard tripped'

// Count focus CROSSINGS between two body-level subtrees, read off `focusout`.
//
// Not `focusin`. Radix's FocusScope re-steals on both events, and the production trace recurses
// through its focusout handler: the nested `element.focus()` runs inside the focusout dispatch, so
// the matching focusin is not dispatched until the stack unwinds. Every focusin the guard sees then
// carries the same target, and a counter reading focusin never moves — measured in WebKit 2272 as
// 574 focusout crossings against 1 on focusin, with the page overflowing anyway.
//
// Not both events either: an ordinary move between two subtrees emits one crossing of each, so
// counting both would halve the effective threshold against legitimate traffic and buy nothing,
// since the focusout of any move is dispatched first. focusout alone keeps sensitivity identical to
// one-crossing-per-move. The `ordinary focus movement` case in www-e2e/src/e2e/focus-trap.spec.ts is
// the concrete version of that: 8 moves stay under the threshold as written and would trip it under
// both-event counting.
//
// Crossings rather than raw focus events is what keeps this off legitimate behaviour: a burst of
// programmatic moves within one container (a form focusing its first invalid field, an
// auto-advancing input) lands entirely in one task with no elapsed time, so a plain per-window
// counter would trip on it. Same-subtree moves score zero here.
const DEFAULT_MAX_CROSSINGS = 10
const DEFAULT_WINDOW_MS = 500
// Long enough for the losing trap to give up, and cut short by the first real user gesture anyway.
const DEFAULT_COOLDOWN_MS = 1_000

// Portalled overlays — Radix's, and Ketch's `#lanyard_root` — mount as children of <body>, so the
// body-level ancestor identifies which side of the fight a node belongs to.
const rootOf = (node: EventTarget | null): Element | null => {
  if (!(node instanceof Element) || node.ownerDocument.body === null) return null
  let current: Element | null = node
  while (current !== null && current.parentElement !== node.ownerDocument.body) {
    current = current.parentElement
  }
  return current
}

// Enough to identify which two subtrees are fighting without capturing anything a user typed.
const describeNode = (node: EventTarget | null): string => {
  if (!(node instanceof Element)) return 'none'
  const id = node.id === '' ? '' : `#${node.id}`
  const classes = Array.from(node.classList).slice(0, 2).join('.')
  return `${node.tagName.toLowerCase()}${id}${classes === '' ? '' : `.${classes}`}`
}

export interface FocusThrashGuardOptions {
  /** Crossings between two body-level subtrees within the window before the guard trips. */
  readonly maxCrossings?: number
  readonly windowMs?: number
  readonly cooldownMs?: number
}

/**
 * Break a focus-trap fight before it overflows the stack. Counts how often focus crosses between two
 * body-level subtrees and, once past the threshold, withholds focus events from the two contenders
 * for a short cooldown so the handlers that re-steal focus cannot run.
 *
 * Returns a function that uninstalls the guard.
 */
export const installFocusThrashGuard = (options: FocusThrashGuardOptions = {}): (() => void) => {
  if (typeof document === 'undefined') return () => undefined

  const {
    maxCrossings = DEFAULT_MAX_CROSSINGS,
    windowMs = DEFAULT_WINDOW_MS,
    cooldownMs = DEFAULT_COOLDOWN_MS,
  } = options

  let windowStartedAt = 0
  let crossings = 0
  // Holds `null` when one side of the war is <body> itself, so that side still participates.
  let contenders: ReadonlySet<Element | null> = new Set()
  let cooldownUntil = 0
  // One event per page load is enough to measure how often this fires and where; a page that keeps
  // re-opening the losing modal can trip repeatedly.
  let reported = false

  // Suppression is scoped to focus CROSSING between the two contenders, not to anything that merely
  // involves one of them. `rootOf` answers with a body-level ancestor, and Gatsby mounts the whole
  // app under one of those (`#___gatsby`), so a war that pairs an overlay against a target in the
  // page makes the entire app a contender. React 17 implements onFocus/onBlur via delegated native
  // focusin/focusout, so suppressing everything touching that contender would take every onFocus and
  // onBlur prop in the app offline for the whole cooldown. A crossing is what the war is made of;
  // movement within one contender is ordinary focus management and must reach its handlers.
  const suppressing = (event: FocusEvent): boolean => {
    if (contenders.size === 0) return false
    // Release on the first event past the cooldown rather than leaving two subtrees — detached by
    // then, if the losing overlay unmounted — pinned for the rest of the page's life.
    if (Date.now() >= cooldownUntil) {
      contenders = new Set()
      return false
    }
    const to = rootOf(event.target)
    const from = rootOf(event.relatedTarget)
    return to !== from && contenders.has(to) && contenders.has(from)
  }

  const handleContendedEvent = (event: FocusEvent): void => {
    if (suppressing(event)) {
      event.stopImmediatePropagation()
    }
  }

  const report = (focusing: string, losing: string): void => {
    if (reported) return
    reported = true
    const path = window.location.pathname
    // Deferred off the current stack on purpose. We are hundreds of frames deep by definition, and
    // Sentry's capture path does its own stack parsing and normalization — running it here risks
    // turning a prevented overflow into a real one.
    setTimeout(() => {
      logAndCaptureMessage(TRIPPED_MESSAGE, {
        level: 'warning',
        // Constant so every trip lands in one issue; the contenders travel in extra data.
        // Interpolating them would fan one problem out into an issue per page, which is exactly what
        // kept the underlying stack overflow invisible for a year.
        fingerprint: [TRIPPED_MESSAGE],
        extra: { path, focusing, losing, maxCrossings, windowMs },
      })
    }, 0)
  }

  const handleFocusOut = (event: FocusEvent): void => {
    if (suppressing(event)) {
      event.stopImmediatePropagation()
      return
    }

    // Focus leaving to nothing — tab-away, window blur, focus entering an iframe — would otherwise
    // read as a crossing against `null`, and a trip on one would pin `null` as a contender and then
    // suppress every later focus-to-nothing inside the other one (usually `#___gatsby`): the
    // app-wide onFocus/onBlur outage the contender scoping exists to prevent. No war shape is lost,
    // because a steal moves focus TO the stealer, so a real crossing always carries a relatedTarget.
    if (!(event.relatedTarget instanceof Element)) return

    const now = Date.now()
    if (now - windowStartedAt > windowMs) {
      windowStartedAt = now
      crossings = 0
    }

    // On focusout the target is losing focus and the relatedTarget is gaining it, so one event
    // carries both sides of the move.
    const from = rootOf(event.target)
    const to = rootOf(event.relatedTarget)
    // Movement inside one subtree is ordinary focus management, however fast it arrives.
    if (from === to) return

    crossings += 1
    if (crossings <= maxCrossings) return

    contenders = new Set([from, to])
    cooldownUntil = now + cooldownMs
    crossings = 0
    // Suppress the event that tripped the guard too: this is the re-steal itself, so stopping it
    // here is what keeps Radix's focusout handler from running the bounce it belongs to.
    event.stopImmediatePropagation()
    report(describeNode(event.relatedTarget), describeNode(event.target))
  }

  // A focus war is programmatic by definition, so a real gesture means it is over. Re-arming here
  // shortens the window in which a trap is inert from the full cooldown to "until the user acts",
  // which matters most for keyboard users. If the war resumes it simply trips again.
  const handleUserGesture = (event: Event): void => {
    if (!event.isTrusted) return
    cooldownUntil = 0
    contenders = new Set()
    crossings = 0
  }

  // Capture phase on `document` is the first node in the propagation path, so stopping here prevents
  // the listeners that do the re-focusing from running at all: Radix registers focusin/focusout in
  // the bubble phase, and Material UI's TrapFocus registers `focus` in the capture phase, behind us
  // because we install before React mounts.
  const contendedEvents = ['focusin', 'focus', 'blur'] as const
  document.addEventListener('focusout', handleFocusOut, true)
  contendedEvents.forEach((type) => document.addEventListener(type, handleContendedEvent, true))
  document.addEventListener('keydown', handleUserGesture, true)
  document.addEventListener('pointerdown', handleUserGesture, true)

  return () => {
    document.removeEventListener('focusout', handleFocusOut, true)
    contendedEvents.forEach((type) =>
      document.removeEventListener(type, handleContendedEvent, true),
    )
    document.removeEventListener('keydown', handleUserGesture, true)
    document.removeEventListener('pointerdown', handleUserGesture, true)
  }
}
